import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Building2, Plus, Trash2 } from "lucide-react";
import {
  fetchCompanyDetails,
  storeOrUpdateCompanyDetails,
  resetCompanyDetailsStatuses,
} from "../../store/slices/companyDetailsSlice";
import { TextField, TextAreaField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const schema = yup.object({
  address: yup.string().nullable(),
  contact_numbers: yup.array().of(yup.string().required("Contact number is required")),
  emails: yup.array().of(yup.string().email("Invalid email").required("Email is required")),
  social_media_links: yup.array().of(
    yup.object({
      platform: yup.string().required("Platform is required"),
      link: yup.string().url("Invalid URL").required("Link is required"),
    })
  ),
});

export default function CompanyDetailsPage() {
  const dispatch = useDispatch();

  const {
    companyDetails,
    companyDetailsFetchStatus,
    companyDetailsSaveStatus,
    loading,
  } = useSelector((state) => state.companyDetails || {});

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      address: "",
      contact_numbers: [""],
      emails: [""],
      social_media_links: [{ platform: "", link: "" }],
    },
  });

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    control,
    name: "contact_numbers",
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "emails",
  });

  const {
    fields: socialFields,
    append: appendSocial,
    remove: removeSocial,
  } = useFieldArray({
    control,
    name: "social_media_links",
  });

  useEffect(() => {
    dispatch(fetchCompanyDetails());
  }, [dispatch]);

  useEffect(() => {
    if (companyDetails && companyDetailsFetchStatus === "success") {
      reset({
        address: companyDetails.address || "",
        contact_numbers: companyDetails.contact_numbers?.length > 0 
          ? companyDetails.contact_numbers 
          : [""],
        emails: companyDetails.emails?.length > 0 
          ? companyDetails.emails 
          : [""],
        social_media_links: companyDetails.social_media_links?.length > 0 
          ? companyDetails.social_media_links 
          : [{ platform: "", link: "" }],
      });
    }
  }, [companyDetails, companyDetailsFetchStatus, reset]);

  const onSubmitHandler = async (data) => {
    // Filter out empty values
    const payload = {
      address: data.address || null,
      contact_numbers: data.contact_numbers.filter(num => num.trim() !== ""),
      emails: data.emails.filter(email => email.trim() !== ""),
      social_media_links: data.social_media_links.filter(
        link => link.platform.trim() !== "" && link.link.trim() !== ""
      ),
    };

    try {
      await dispatch(storeOrUpdateCompanyDetails(payload)).unwrap();
      showApiSuccess(
        companyDetails 
          ? "Company details updated successfully" 
          : "Company details created successfully"
      );
      dispatch(fetchCompanyDetails());
    } catch (error) {
      showApiError("Failed to save company details", error);
    }
  };

  if (companyDetailsFetchStatus === "loading" && !companyDetails) {
    return <Loader />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {loading && <Loader />}
      <div className="theme-card rounded-lg p-6">
        <div className="mb-6 flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Company Details</h1>
            <p className="text-gray-600 mt-1">
              Manage your company information and contact details
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
          {/* Address */}
          <TextAreaField
            label="Address"
            rows={3}
            placeholder="Enter company address"
            {...register("address")}
            error={errors?.address?.message}
          />

          {/* Contact Numbers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Numbers
            </label>
            <div className="space-y-2">
              {contactFields.map((field, index) => (
                <div key={field.id} className="flex items-start space-x-2">
                  <div className="flex-1">
                    <TextField
                      placeholder="Enter contact number"
                      {...register(`contact_numbers.${index}`)}
                      error={errors?.contact_numbers?.[index]?.message}
                    />
                  </div>
                  {contactFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContact(index)}
                      className="mt-2 text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => appendContact("")}
              className="mt-2 flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Contact Number</span>
            </button>
          </div>

          {/* Emails */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Addresses
            </label>
            <div className="space-y-2">
              {emailFields.map((field, index) => (
                <div key={field.id} className="flex items-start space-x-2">
                  <div className="flex-1">
                    <TextField
                      type="email"
                      placeholder="Enter email address"
                      {...register(`emails.${index}`)}
                      error={errors?.emails?.[index]?.message}
                    />
                  </div>
                  {emailFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEmail(index)}
                      className="mt-2 text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => appendEmail("")}
              className="mt-2 flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Email</span>
            </button>
          </div>

          {/* Social Media Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Social Media Links
            </label>
            <div className="space-y-3">
              {socialFields.map((field, index) => (
                <div key={field.id} className="flex items-start space-x-2">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <TextField
                      placeholder="Platform (e.g., Facebook)"
                      {...register(`social_media_links.${index}.platform`)}
                      error={errors?.social_media_links?.[index]?.platform?.message}
                    />
                    <TextField
                      placeholder="URL"
                      {...register(`social_media_links.${index}.link`)}
                      error={errors?.social_media_links?.[index]?.link?.message}
                    />
                  </div>
                  {socialFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSocial(index)}
                      className="mt-2 text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => appendSocial({ platform: "", link: "" })}
              className="mt-2 flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Social Media Link</span>
            </button>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {companyDetails ? "Update Company Details" : "Save Company Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
